/**
 * フォーム回答の統計セクション hooks (v3.1.4)。
 */
import type {
  FormAnalyticsMeta,
  FormAnalyticsSection,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface FormAnalyticsSectionRecord extends FormAnalyticsSection {
  id: string;
}

export function useFormAnalyticsSections(formId: string | null | undefined) {
  return useQuery({
    queryKey: ['form_analytics_sections', formId],
    enabled: !!formId,
    queryFn: async (): Promise<FormAnalyticsSectionRecord[]> => {
      if (!formId) return [];
      const snap = await getDocs(
        query(
          collection(db, 'forms', formId, 'analytics_sections'),
          orderBy('displayOrder', 'asc'),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as FormAnalyticsSection),
      }));
    },
  });
}

export function useFormAnalyticsMeta(formId: string | null | undefined) {
  return useQuery({
    queryKey: ['form_analytics_meta', formId],
    enabled: !!formId,
    queryFn: async (): Promise<FormAnalyticsMeta | null> => {
      if (!formId) return null;
      const snap = await getDoc(
        doc(db, 'forms', formId, 'analytics_meta', 'main'),
      );
      if (!snap.exists()) return null;
      return snap.data() as FormAnalyticsMeta;
    },
  });
}

export function useUpsertAnalyticsSection(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      title?: string | null;
      chartType: FormAnalyticsSection['chartType'];
      fieldIds: string[];
      displayOrder: number;
      uid: string;
    }) => {
      const ref = input.id
        ? doc(db, 'forms', formId, 'analytics_sections', input.id)
        : doc(collection(db, 'forms', formId, 'analytics_sections'));
      const data: Record<string, unknown> = {
        title: input.title ?? null,
        chartType: input.chartType,
        fieldIds: input.fieldIds,
        displayOrder: input.displayOrder,
        _deleted: false,
        updatedAt: serverTimestamp(),
      };
      if (!input.id) {
        data.createdAt = serverTimestamp();
        data.createdBy = input.uid;
      }
      await setDoc(ref, data, { merge: true });
      return { id: ref.id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form_analytics_sections', formId] });
    },
  });
}

export function useDeleteAnalyticsSection(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sectionId: string) => {
      await deleteDoc(
        doc(db, 'forms', formId, 'analytics_sections', sectionId),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form_analytics_sections', formId] });
    },
  });
}

export function useUpsertAnalyticsMeta(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      visualStyle?: FormAnalyticsMeta['visualStyle'];
      publicToken?: string | null;
      uid: string;
    }) => {
      const ref = doc(db, 'forms', formId, 'analytics_meta', 'main');
      const patch: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
        updatedBy: input.uid,
      };
      if (input.visualStyle !== undefined) patch.visualStyle = input.visualStyle;
      if (input.publicToken !== undefined) patch.publicToken = input.publicToken;
      await setDoc(ref, patch, { merge: true });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form_analytics_meta', formId] });
    },
  });
}

// 公開ビュー (認証不要)
import type { PublicFormAnalyticsView } from '@goenhub/shared';

const getPublicAnalyticsFn = httpsCallable<
  { token: string },
  | { status: 'ok'; analytics: PublicFormAnalyticsView }
  | { status: 'expired' }
  | { status: 'not_found' }
>(functions, 'getPublicFormAnalytics');

export function usePublicFormAnalytics(token: string | null) {
  return useQuery({
    queryKey: ['public_form_analytics', token],
    enabled: !!token,
    queryFn: async () => {
      if (!token) return null;
      const r = await getPublicAnalyticsFn({ token });
      return r.data;
    },
  });
}
